export const DEFAULT_CONTENT = {
  hero: {
    eyebrow: "Move from HP Poly Gen 1 to Gen 2",
    headlineLines: ["Prepare Your Meeting Rooms", "for What's Next"],
    paragraph: "Build a more capable, responsive and future-ready collaboration environment — powered by HP Poly VideoOS 5.0. Designed for today. Built for what's next.",
    image: "/hero-banner.jpg",
  },
  banner: {
    eyebrow: "Gen 2 at a glance",
    title: "What You Get with HP Poly Gen 2",
    paragraph: "Next-generation video, audio and compute — engineered on HP Poly VideoOS 5.0 and designed to support your collaboration needs for longer.",
    chips: ["Smarter video", "Enhanced audio", "Upgraded performance"],
    ctaText: "Connect with your HP Poly specialist",
    image: "/assets/img/benefits-gen2.jpg",
  },
  marquee: {
    items: ["Microsoft Teams Rooms", "Zoom Rooms", "Google Meet", "HP Poly Lens", "HP Poly VideoOS 5.0", "Android 13 Enterprise", "Support Through 2032", "HP Poly DirectorAI"],
  },
  countdown: {
    items: [
      { label: "Microsoft Teams certification for Gen 1 devices ended September 3, 2026", date: "2026-09-03" },
      { label: "Microsoft Teams support for Gen 1 devices ends September 3, 2027", date: "2027-09-03" },
      { label: "Zoom Rooms support for Gen 1 devices ends September 2028", date: "2028-09-01" },
    ],
  },
  whyMove: {
    eyebrow: "Why move to Gen 2?",
    title: "Your Collaboration Environment Is Evolving. Your Technology Should Too.",
    lead: "Since its launch in 2019, HP Poly Studio X has helped teams connect through all-in-one video collaboration. Today, evolving meeting platforms and rising user expectations demand more from room technology. If your organisation uses Gen 1 devices, now is a good time to review their support timelines and plan your next step—giving your team time to budget, prioritise and upgrade on your schedule.",
    benefits: [
      "Improve meeting experiences with smarter video and enhanced audio capabilities",
      "Increase performance with upgraded compute and memory",
      "Prepare for evolving platform requirements with a new Android-based foundation",
      "Simplify future planning with a platform designed for long-term support",
      "Standardize your meeting rooms around next-generation collaboration technology",
    ],
  },
  videoos: {
    eyebrow: "The new foundation",
    title: "Future-Ready Video Collaboration with HP Poly VideoOS 5.0",
    lead: "Gen 2 devices come with HP Poly VideoOS 5.0, a major platform upgrade to Android 13. The new architecture is designed to support future innovations, enhance long-term performance and extend lifecycle support for HP Poly's next-generation Studio video solutions.",
    cards: [
      { title: "Android 13 Foundation", desc: "Gen 2 moves to Android 13, providing a modern platform foundation for next-generation collaboration experiences." },
      { title: "Certified for Leading Platforms", desc: "HP Poly's next-generation video portfolio is certified for Microsoft Teams and Zoom on Android 13." },
      { title: "Ready for Future Innovation", desc: "VideoOS 5.0 lays the foundation for future updates, including HP Poly DirectorAI multi-camera support with VideoOS 5.1." },
      { title: "Designed for Long-Term Support", desc: "The VideoOS platform strategy establishes a foundation for future updates to Android 17 and support through 2032." },
    ],
  },
  upgradeBanner: {
    eyebrow: "The case for now",
    title: "Your Gen 1 Fleet Won't Wait. Neither Should You.",
    lead: "Platform certification dates are fixed — budgets and refresh cycles aren't. Upgrade to Gen 2 on your terms: planned, phased and disruption-free, instead of reacting when support runs out.",
    deadlines: ["Teams support ends September 2027", "Google Meet support ended July 2026", "Zoom final release June 2027", "Zoom support ends September 2028"],
    ctaText: "Connect with your HP Poly specialist",
  },
  whatsBetter: {
    eyebrow: "What's better with Gen 2?",
    title: "A Better Meeting Experience. Stronger Performance.",
    lead: "Gen 2 combines a new software foundation with enhanced video, audio and performance capabilities to create a more seamless collaboration experience.",
    pillars: [
      { title: "Smarter Video Experiences", desc: "Gen 2 devices introduce advanced video capabilities designed to help participants stay visible, engaged and naturally part of every meeting.", points: ["Higher-resolution camera sensors", "Improved low-light performance", "More intelligent speaker and group framing", "Smarter camera framing"] },
      { title: "Enhanced Audio Performance", desc: "Advanced audio intelligence is designed to deliver clearer, more natural conversations across modern collaboration spaces, including medium and larger rooms.", points: ["Enhanced AI noise blocking", "Multi-mic audio mixing", "Clearer voice pickup", "Improved speech clarity", "Better performance in dynamic collaboration spaces"] },
      { title: "More Powerful Performance", desc: "With upgraded compute and memory capabilities, Gen 2 devices are designed to deliver a faster and more responsive meeting experience.", points: ["Faster device wake-up", "Smoother user interface responsiveness", "Faster touch controller performance", "Better support for advanced collaboration scenarios", "Improved handling of multi-stream meetings"] },
    ],
  },
  specTables: {
    eyebrow: "Gen 2 in detail",
    title: "Compare the Gen 2 Capabilities",
    tabs: [
      {
        label: "Smarter Video Experiences",
        headers: ["Gen 2 Device", "Camera Specifications", "Zoom Capability", "DirectorAI Capabilities"],
        rows: [
          ["HP Poly Studio X32", "4K Ultra HD 20MP camera", "5x digital zoom", "People Framing, Group Framing, Speaker Framing, Perimeter"],
          ["HP Poly Studio X52", "4K Ultra HD 20MP camera", "5x digital zoom", "People Framing, Group Framing, Speaker Framing, Perimeter"],
          ["HP Poly Studio X72", "Dual 4K 20MP cameras (70° + 120° FOV)", "7.3x digital zoom", "People Framing, Group Framing, Speaker Framing, Advanced Multi-Camera Intelligence, Perimeter"],
        ],
      },
      {
        label: "Enhanced Audio Performance",
        headers: ["Area", "Gen 2 (X32/X52/X72)", "Customer Benefit"],
        rows: [
          ["AI Noise Suppression", "Enhanced NoiseBlockAI with newer AI/ML processing and sound reflection reduction", "Better removal of distracting background noises"],
          ["Acoustic Boundary", "Enhanced Acoustic Fence", "Improved voice isolation and reduced room distractions"],
          ["Audio Processing", "Newer AI-driven audio processing", "More natural conversations and improved clarity"],
          ["Microphone Technology", "Advanced beamforming microphone architecture", "Better speaker pickup and voice localization"],
          ["Hybrid Meeting Experience", "AI-enhanced speaker tracking and framing working together", "Audio and video remain synchronized around active speakers"],
          ["Stereo Audio", "Improved stereo speaker design across Gen 2 portfolio", "Fuller room-filling audio experience"],
        ],
      },
      {
        label: "More Powerful Performance",
        headers: ["Area", "Gen 2 (X32/X52/X72)", "Customer Benefit"],
        rows: [
          ["Compute Platform", "New-generation Qualcomm collaboration platform", "Greater processing headroom for AI-driven audio and video experiences"],
          ["AI Workloads", "Enhanced support for advanced DirectorAI features and future AI enhancements", "Better meeting intelligence and scalability"],
          ["Video Processing", "Enhanced multi-stream and multi-camera processing capabilities", "Improved framing accuracy and participant experience"],
          ["Operating System Support", "Newer hardware foundation designed for future Android and collaboration platform updates", "Longer platform longevity"],
          ["Peripheral Expansion", "Improved support for Gen 2 peripherals including A2 bridge and A2 Mics, Studio E60, TC10 and modular room solutions", "Increased room design flexibility"],
          ["System Responsiveness", "Increased compute resources for AI, video processing and future features", "Better ability to support evolving workloads"],
        ],
      },
    ],
  },
  longTerm: {
    eyebrow: "Built for the long term",
    title: "One New Platform Foundation. More Room to Plan Ahead.",
    lead: "The move to Gen 2 is more than a device refresh. It gives organizations a new platform foundation designed to support the evolution of collaboration technology over the longer term. With HP Poly VideoOS 5.0, Gen 2 provides a pathway toward future platform updates while supporting the continued evolution of video and audio experiences.",
    supportLabel: "Designed for Support Through 2032",
    supportText: "The planned evolution from Android 13 toward Android 17 is part of HP Poly's lifecycle commitment to customers, helping provide continued device support in collaboration with platform partners through 2032.",
    timeline: [
      { year: "Today", label: "VideoOS 5.0 on Android 13" },
      { year: "5.1", label: "DirectorAI multi-camera support" },
      { year: "A17", label: "Planned evolution toward Android 17" },
      { year: "2032", label: "Device support with platform partners" },
    ],
    items: [
      { title: "Long-Term Planning", desc: "Plan meeting room refreshes with greater visibility into the technology roadmap." },
      { title: "Platform Evolution", desc: "Stay aligned with evolving collaboration platform requirements." },
      { title: "Consistent Experiences", desc: "Build a standardized collaboration environment around next-generation devices." },
      { title: "Future Innovation", desc: "Create a foundation designed to support future VideoOS capabilities and experiences." },
    ],
  },
  milestones: {
    eyebrow: "Why plan your migration now?",
    title: "Don't Let Your Next Technology Refresh Become a Deadline.",
    lead: "As collaboration platforms evolve, some Gen 1 devices are approaching important certification and support milestones. Understanding where your existing devices stand today can help you make informed decisions about when and how to refresh your meeting rooms.",
    tableTitle: "Key Collaboration Platform Milestones",
    rows: [
      { platform: "Microsoft Teams", milestone: "Certification ended September 3, 2026. Support ends September 3, 2027.", urgency: "High" },
      { platform: "Google Meet", milestone: "Support ended in July 2026 for applicable Studio X30, X50 and X70 / Android 10 devices.", urgency: "High" },
      { platform: "Zoom Rooms", milestone: "Certification ended September 2025. Support ends September 2028. Final software release: June 2027.", urgency: "Medium" },
    ],
    planTitle: "The Opportunity Is to Plan Before You Have to.",
    planLead: "Many organizations are already beginning to act. Join them ahead of the deadlines.",
    actions: ["Assess existing Gen 1 deployments", "Prioritize meeting room refresh plans", "Standardize future collaboration environments"],
  },
  matrix: {
    eyebrow: "Find your Gen 2 replacement",
    title: "Your Gen 1 Device Has a Gen 2 Path Forward",
    lead: "Moving to Gen 2 doesn't have to mean starting from scratch. Identify your current HP Poly devices and explore their next-generation replacements.",
    mappings: [
      { gen1: "G7500", gen2: "G62", benefit: "Compact form factor, PoE powered, dual display 4K60 — the modular engine for custom and executive rooms." },
      { gen1: "Studio X30", gen2: "Studio X32", benefit: "Expanded mic pickup, upgraded 4K sensor and native touch join — built for small and huddle rooms." },
      { gen1: "Studio X50", gen2: "Studio X52", benefit: "110° field of view, 20MP sensor and blended acoustic boundary — the medium-room workhorse." },
      { gen1: "Studio X70", gen2: "Studio X72", benefit: "Dual-camera optics, dual 4K sensors and automated presenter tracking — made for large rooms." },
      { gen1: "TC8", gen2: "TC10", benefit: "Integrated room scheduling LED indicators and flexible PoE mounting — the next-gen touch controller." },
    ],
  },
  steps: {
    eyebrow: "Make the move with a plan",
    title: "From Gen 1 to Gen 2: A Clear Path Forward",
    lead: "A planned migration can help organizations move from reactive device replacement to a more structured approach to meeting room modernization.",
    steps: [
      { name: "Assess", desc: "Understand your existing Gen 1 deployment and identify devices approaching relevant certification, support or lifecycle milestones." },
      { name: "Prioritize", desc: "Determine which meeting rooms and devices should be refreshed first based on business requirements, platform needs and device lifecycle." },
      { name: "Map", desc: "Match your existing Gen 1 devices to the appropriate Gen 2 solutions." },
      { name: "Standardize", desc: "Create a more consistent meeting room environment built around next-generation collaboration technology." },
      { name: "Deploy", desc: "Transition to Gen 2 with the right deployment approach and support." },
    ],
  },
  rooms: {
    eyebrow: "Choose the right solution",
    title: "Built for the Way Your Meeting Rooms Work",
    lead: "From smaller huddle spaces to larger collaboration environments, Gen 2 solutions can help organizations create consistent, high-quality meeting experiences across their meeting room estate.",
    rooms: [
      { type: "Small / huddle rooms", device: "HP Poly Studio X32 + TC10", img: "/assets/img/room-huddle.png" },
      { type: "Medium meeting rooms", device: "HP Poly Studio X52 + TC10", img: "/assets/img/room-medium.jpg" },
      { type: "Large meeting rooms", device: "HP Poly Studio X72 + Dual Display", img: "/assets/img/room-large.jpg" },
      { type: "Flexible / executive collaboration spaces", device: "HP Poly G62 Modular System", img: "/assets/img/room-flexible.jpg" },
    ],
  },
  leadForm: {
    eyebrow: "Ready to plan your move?",
    title: "Ready to Plan Your Move to Gen 2?",
    lead: "Your meeting rooms need technology that can support the way your teams collaborate today and the way they will collaborate tomorrow.",
    checklist: ["Assess your Gen 1 deployment", "Identify your priorities", "Plan your move to Gen 2"],
    note: "Share a few details and a collaboration specialist will help you map your Gen 1 estate to the right Gen 2 path — including certification deadlines that apply to your platform.",
    formTitle: "Start Your Gen 2 Migration",
    formSub: "Fields marked * are required. We respond within one business day.",
    submitLabel: "Connect with your HP Poly specialist",
    successTitle: "Your Migration Plan Request Is In.",
    successBody: "Thanks, {name}. We've received your details and a specialist will reach out to {email} with your Gen 2 migration assessment.",
  },
};
